import * as CookieConsent from 'vanilla-cookieconsent'
import 'vanilla-cookieconsent/dist/cookieconsent.css'
import './cookieConsent.css'

// Klíč i revize musí sedět s inline skriptem v resources/views/app.blade.php,
// který volbu čte ještě před načtením GTM. Souhlas patří kontejneru
// konkrétního provozovatele, ne celému Parkovatu.
const storageKeyFor = (gtmId) => `cc_parkovato_${gtmId}`
const REVISION = 0

const LOCALES = ['cs', 'en', 'de', 'pl']

let started = false

// Události čekající na rozhodnutí o souhlasu, viz whenConsentDecided().
let pendingUntilDecided = new Set()

function acceptedCategories() {
  return ['necessary', 'analytics', 'marketing'].filter((category) => CookieConsent.acceptedCategory(category))
}

function consentState() {
  const analytics = CookieConsent.acceptedCategory('analytics') ? 'granted' : 'denied'
  const marketing = CookieConsent.acceptedCategory('marketing') ? 'granted' : 'denied'

  // personalization_storage zůstává zamítnutý z výchozího stavu, žádnou
  // personalizaci obsahu lišta nenabízí.
  return {
    analytics_storage: analytics,
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
  }
}

// Volá se po první volbě i po každé změně, včetně odvolání souhlasu.
// Událost v dataLayeru spustí značky, které na souhlas čekají (třeba pixel
// OpenAI, který Consent Mode sám nečte).
function syncConsent() {
  window.dataLayer = window.dataLayer || []
  const gtag =
    window.gtag ||
    function () {
      window.dataLayer.push(arguments)
    }

  gtag('consent', 'update', consentState())
  window.dataLayer.push({ event: 'consent_update', accepted_categories: acceptedCategories() })
}

const escapeHtml = (value) => String(value).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char])

// Footer lišty je HTML, odkaz proto jen na naši cestu k souboru a escapovaný.
function privacyLink(url, label) {
  if (typeof url !== 'string' || !url.startsWith('/storage/')) return ''
  return `<a href="${escapeHtml(url)}" target="_blank" rel="noopener">${escapeHtml(label)}</a>`
}

function translation({ title, description, acceptAll, acceptNecessary, settings, save, close, sections, privacy }, privacyUrl) {
  return {
    consentModal: {
      title,
      description,
      acceptAllBtn: acceptAll,
      acceptNecessaryBtn: acceptNecessary,
      showPreferencesBtn: settings,
      footer: privacyLink(privacyUrl, privacy),
    },
    preferencesModal: {
      title: settings,
      acceptAllBtn: acceptAll,
      acceptNecessaryBtn: acceptNecessary,
      savePreferencesBtn: save,
      closeIconLabel: close,
      sections: [{ description }, { title: sections.necessary[0], description: sections.necessary[1], linkedCategory: 'necessary' }, { title: sections.analytics[0], description: sections.analytics[1], linkedCategory: 'analytics' }, { title: sections.marketing[0], description: sections.marketing[1], linkedCategory: 'marketing' }],
    },
  }
}

const TEXTS = {
  cs: {
    title: 'Souhlas s cookies',
    description: 'Rezervační systém Parkovato a provozovatel parkoviště používají cookies. Nezbytné zajišťují fungování rezervace. Analytické a marketingové použijeme jen s vaším souhlasem, který můžete kdykoli změnit.',
    acceptAll: 'Přijmout vše',
    acceptNecessary: 'Jen nezbytné',
    settings: 'Nastavení cookies',
    save: 'Uložit nastavení',
    close: 'Zavřít',
    privacy: 'Zásady zpracování osobních údajů',
    sections: {
      necessary: ['Nezbytné', 'Bez nich nejde rezervaci dokončit. Pamatují si například zvolený jazyk a rozpracovanou rezervaci.'],
      analytics: ['Analytické', 'Pomáhají provozovateli pochopit, jak zákazníci rezervační formulář používají.'],
      marketing: ['Marketingové', 'Umožňují měřit úspěšnost reklam a zobrazovat relevantnější nabídky.'],
    },
  },
  en: {
    title: 'Cookie consent',
    description: 'The Parkovato booking system and the parking operator use cookies. Necessary cookies keep the booking working. We use analytics and marketing cookies only with your consent, which you can change at any time.',
    acceptAll: 'Accept all',
    acceptNecessary: 'Necessary only',
    settings: 'Cookie settings',
    save: 'Save settings',
    close: 'Close',
    privacy: 'Privacy policy',
    sections: {
      necessary: ['Necessary', 'The booking cannot be completed without them. They remember, for example, your language and your booking in progress.'],
      analytics: ['Analytics', 'Help the operator understand how customers use the booking form.'],
      marketing: ['Marketing', 'Allow measuring ad performance and showing more relevant offers.'],
    },
  },
  de: {
    title: 'Cookie-Einwilligung',
    description: 'Das Buchungssystem Parkovato und der Parkplatzbetreiber verwenden Cookies. Notwendige Cookies halten die Buchung am Laufen. Analyse- und Marketing-Cookies setzen wir nur mit Ihrer Einwilligung ein, die Sie jederzeit ändern können.',
    acceptAll: 'Alle akzeptieren',
    acceptNecessary: 'Nur notwendige',
    settings: 'Cookie-Einstellungen',
    save: 'Einstellungen speichern',
    close: 'Schließen',
    privacy: 'Datenschutzerklärung',
    sections: {
      necessary: ['Notwendig', 'Ohne sie lässt sich die Buchung nicht abschließen. Sie speichern zum Beispiel die Sprache und die laufende Buchung.'],
      analytics: ['Analyse', 'Helfen dem Betreiber zu verstehen, wie Kunden das Buchungsformular nutzen.'],
      marketing: ['Marketing', 'Ermöglichen die Messung von Werbung und die Anzeige relevanterer Angebote.'],
    },
  },
  pl: {
    title: 'Zgoda na pliki cookie',
    description: 'System rezerwacji Parkovato i operator parkingu używają plików cookie. Niezbędne zapewniają działanie rezerwacji. Analityczne i marketingowe stosujemy tylko za Twoją zgodą, którą możesz w każdej chwili zmienić.',
    acceptAll: 'Akceptuj wszystkie',
    acceptNecessary: 'Tylko niezbędne',
    settings: 'Ustawienia cookie',
    save: 'Zapisz ustawienia',
    close: 'Zamknij',
    privacy: 'Polityka prywatności',
    sections: {
      necessary: ['Niezbędne', 'Bez nich nie można dokończyć rezerwacji. Zapamiętują na przykład wybrany język i rozpoczętą rezerwację.'],
      analytics: ['Analityczne', 'Pomagają operatorowi zrozumieć, jak klienci korzystają z formularza rezerwacji.'],
      marketing: ['Marketingowe', 'Umożliwiają pomiar skuteczności reklam i wyświetlanie trafniejszych ofert.'],
    },
  },
}

export function initCookieConsent({ gtmId, locale, privacyUrls }) {
  if (started) return
  started = true

  const translations = {}
  LOCALES.forEach((code) => {
    translations[code] = translation(TEXTS[code], privacyUrls?.[code])
  })

  CookieConsent.run({
    mode: 'opt-in',
    revision: REVISION,
    hideFromBots: true,
    disablePageInteraction: true,

    // localStorage místo cookie: průvodce běží i v iframu na webech
    // provozovatelů, kde prohlížeče cookies třetích stran blokují a lišta by
    // se ukazovala při každém načtení.
    cookie: {
      name: storageKeyFor(gtmId),
      useLocalStorage: true,
      expiresAfterDays: 182,
    },

    guiOptions: {
      consentModal: { layout: 'box wide', position: 'bottom center', equalWeightButtons: true },
      preferencesModal: { layout: 'box', equalWeightButtons: true },
    },

    categories: {
      necessary: { enabled: true, readOnly: true },
      analytics: {
        autoClear: { cookies: [{ name: /^_ga/ }, { name: '_gid' }, { name: /^_clck/ }, { name: /^_clsk/ }] },
      },
      marketing: {
        autoClear: { cookies: [{ name: /^_gcl_/ }, { name: /^_fb/ }, { name: /^__sznet/ }, { name: /^__oppref/ }] },
      },
    },

    onFirstConsent: () => {
      syncConsent()
      const pending = [...pendingUntilDecided]
      pendingUntilDecided.clear()
      pending.forEach((callback) => callback())
    },
    onChange: syncConsent,

    language: {
      default: LOCALES.includes(locale) ? locale : 'cs',
      translations,
    },
  })
}

// Měřicí událost pošle až s rozhodnutým souhlasem. Nový návštěvník uvidí
// lištu, která stránku blokuje, a zobrazení kroku by jinak odešlo dřív, než
// klikne, takže značky mimo Google by se na prvním kroku nespustily vůbec.
// Kdo lištu zavře bez volby, událost nepošle. Dokončenou rezervaci proto
// tudy neposílat, ta se musí započítat vždy. Vrací funkci, která čekající
// událost zruší. Stránka ji volá v onUnmounted, aby po odchodu z kroku
// neodešlo zobrazení kroku, na kterém už zákazník není.
export function whenConsentDecided(callback) {
  if (!started || CookieConsent.validConsent()) {
    callback()
    return () => {}
  }

  pendingUntilDecided.add(callback)
  return () => pendingUntilDecided.delete(callback)
}

// Výchozí stav z resources/views/app.blade.php, musí sedět.
const CONSENT_DEFAULT = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
  personalization_storage: 'denied',
  functionality_storage: 'granted',
  security_storage: 'granted',
  wait_for_update: 500,
}

// Stav souhlasu uložený pro kontejner, předaný před jeho načtením. Stejné
// kontroly jako čtení v resources/views/app.blade.php, ale vždy s explicitní
// hodnotou: dřívější update jiného kontejneru ve stejném dataLayeru se
// nesmí zdědit. Obnovený iframe mohl přijít z dokumentu bez GTM, kde
// hlavička výchozí stav vůbec nenastavila.
export function applySavedConsent(gtmId) {
  let categories = []
  try {
    const saved = JSON.parse(window.localStorage.getItem(storageKeyFor(gtmId)) || 'null')
    const valid = saved && Array.isArray(saved.categories) && typeof saved.consentId === 'string' && saved.consentId && saved.consentTimestamp && saved.lastConsentTimestamp && saved.revision === REVISION && saved.expirationTime > Date.now()
    if (valid) categories = saved.categories
  } catch (e) {}

  const analytics = categories.includes('analytics') ? 'granted' : 'denied'
  const marketing = categories.includes('marketing') ? 'granted' : 'denied'

  window.dataLayer = window.dataLayer || []
  const gtag =
    window.gtag ||
    function () {
      window.dataLayer.push(arguments)
    }

  if (!window.parkovatoConsentDefault) {
    window.parkovatoConsentDefault = true
    gtag('consent', 'default', CONSENT_DEFAULT)
  }

  gtag('consent', 'update', {
    analytics_storage: analytics,
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
  })
}

// Údaje zákazníka pro vylepšené konverze smí odejít jen s marketingovým
// souhlasem. Parkoviště bez GTM se neměří vůbec. Když lištu zobrazuje
// kontejner, umíme volbu přečíst jen z Cookiebotu, u jiné CMP se údaje
// raději nepošlou.
export function hasMarketingConsent() {
  if (started) return CookieConsent.acceptedCategory('marketing')
  return window.Cookiebot?.consent?.marketing === true
}

export function setCookieConsentLanguage(locale) {
  if (started && LOCALES.includes(locale)) {
    CookieConsent.setLanguage(locale)
  }
}

export function showCookiePreferences() {
  if (started) {
    CookieConsent.showPreferences()
  }
}

export function isCookieConsentActive() {
  return started
}
